import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { connect } from 'react-redux';

import MyAccountQuery from 'Query/MyAccount.query';
import { fetchQuery } from 'Util/Request';

import FieldPostcodeCity from './FieldPostcodeCity.component';

/** @namespace PeggysagePwa/Component/FieldPostcodeCity/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    defaultCountry: state.ConfigReducer.default_country
});

/** @namespace PeggysagePwa/Component/FieldPostcodeCity/Container/mapDispatchToProps */
export const mapDispatchToProps = () => ({});

/** @namespace PeggysagePwa/Component/FieldPostcodeCity/Container */
export class FieldPostcodeCityContainer extends PureComponent {
    static propTypes = {
        defaultCountry: PropTypes.string,
        defaultValues: PropTypes.shape({
            postcode: PropTypes.string,
            city: PropTypes.string
        }).isRequired,
        validatePostcode: PropTypes.func,
        onPostcodeChange: PropTypes.func
    };

    static defaultProps = {
        defaultCountry: 'FR',
        validatePostcode: null,
        onPostcodeChange: null
    };

    state = {
        postcodeValue: '',
        cityOptions: [],
        cityValue: '',
        isLoadingSuggestions: false,
        postcodeLength: null,
        hasNoResult: false
    };

    containerFunctions = {
        onPostcodeChange: this.onPostcodeChange.bind(this),
        onCityChange: this.onCityChange.bind(this),
        validatePostcodeField: this.validatePostcodeField.bind(this)
    };

    // eslint-disable-next-line no-magic-numbers
    debouncedLoadSuggestions = debounce(this.loadSuggestions, 200);

    componentDidMount() {
        const { defaultValues: { postcode } } = this.props;

        this.setPostcodeMaxLength();

        if (postcode) {
            this.setState({ postcodeValue: postcode });
            this.loadSuggestions(postcode);
        }
    }

    componentDidUpdate(prevProps) {
        const { defaultValues: { postcode }, defaultCountry } = this.props;
        const { defaultValues: { postcode: prevPostcode }, defaultCountry: prevDefaultCountry } = prevProps;

        if (postcode !== prevPostcode) {
            this.setState({ postcodeValue: postcode });
            this.loadSuggestions(postcode);
        }

        if (defaultCountry !== prevDefaultCountry) {
            this.setPostcodeMaxLength();
        }
    }

    containerProps() {
        const {
            postcodeValue,
            cityOptions,
            cityValue,
            isLoadingSuggestions,
            postcodeLength,
            hasNoResult
        } = this.state;

        return {
            postcodeValue,
            cityOptions,
            cityValue,
            isLoadingSuggestions,
            postcodeLength,
            hasNoResult
        };
    }

    setPostcodeMaxLength() {
        const { defaultCountry } = this.props;

        const postcodeLengthByCountry = {
            BE: 4,
            CH: 4,
            DE: 5,
            ES: 5,
            FR: 5,
            IT: 5
        };

        this.setState({
            postcodeLength: (defaultCountry && postcodeLengthByCountry[defaultCountry]) || null
        });
    }

    async getSuggestions(value) {
        if (this.getSuggestionsAbortController) {
            this.getSuggestionsAbortController.abort();
        }
        this.getSuggestionsAbortController = new AbortController();

        return fetchQuery(
            MyAccountQuery.getCityListForZipCode(value),
            this.getSuggestionsAbortController.signal
        ).then(
            /** @namespace PeggysagePwa/Component/FieldPostcodeCity/Container/FieldPostcodeCityContainer/getSuggestions/then/catch/fetchQuery/then */
            (res) => {
                this.getSuggestionsAbortController = null;
                return res.getCityListForZipCode;
            }
        ).catch(
            /** @namespace PeggysagePwa/Component/FieldPostcodeCity/Container/FieldPostcodeCityContainer/getSuggestions/then/catch */
            (err) => {
                if (!(err instanceof DOMException && err.name === 'AbortError')) {
                    throw err;
                }

                return [];
            }
        );
    }

    loadSuggestions(value) {
        const { defaultValues: { city } } = this.props;
        const { postcodeLength } = this.state;

        // eslint-disable-next-line no-magic-numbers
        if (value.length > 3) {
            this.setState({ isLoadingSuggestions: true });

            this.getSuggestions(value).then(
                /** @namespace PeggysagePwa/Component/FieldPostcodeCity/Container/FieldPostcodeCityContainer/loadSuggestions/then/catch/getSuggestions/then */
                (result) => {
                    const cityOptions = result ? result.map(({ place_name: city }) => ({
                        value: city,
                        label: city
                    })) : [];

                    this.setState({
                        cityOptions,
                        cityValue: cityOptions && city ? cityOptions.find(
                            (option) => option.label.toLowerCase() === city.toLowerCase()
                        )?.value || '' : '',
                        isLoadingSuggestions: false,
                        hasNoResult: value.length === postcodeLength && !cityOptions.length
                    });
                }
            ).catch(
                /** @namespace PeggysagePwa/Component/FieldPostcodeCity/Container/FieldPostcodeCityContainer/loadSuggestions/then/catch */
                () => {
                    this.setState({ isLoadingSuggestions: false, hasNoResult: false });
                }
            );
        } else {
            this.setState({ cityOptions: [], cityValue: '', hasNoResult: false });
        }
    }

    onPostcodeChange(e, field) {
        const { target: { value } } = e;
        const { onPostcodeChange } = this.props;

        // Keep only numbers, remove other characters
        const postcodeValue = value.replace(/\D/g, '');

        this.setState({ postcodeValue, hasNoResult: false });

        this.debouncedLoadSuggestions(postcodeValue);

        if (onPostcodeChange) {
            onPostcodeChange(e, field);
        }
    }

    onCityChange(city) {
        this.setState({ cityValue: city });
    }

    validatePostcodeField(value) {
        const { validatePostcode } = this.props;
        const { postcodeLength } = this.state;

        if (!postcodeLength) {
            return true;
        }

        if (value.length !== postcodeLength) {
            return __('Minimum %s characters!', postcodeLength);
        }

        if (validatePostcode) {
            return validatePostcode(value);
        }

        return true;
    }

    render() {
        return (
            <FieldPostcodeCity
              { ...this.containerFunctions }
              { ...this.containerProps() }
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(FieldPostcodeCityContainer);
